import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { csrfHeaders } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { Check, X, AlertTriangle, Copy, Trash2, Calendar as CalIcon, Wallet, Clock, Users, Download, Star, Banknote, CornerUpLeft, Plus, FileText, Crown, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useSearchParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import { useConfirm } from "../components/ConfirmProvider";
import { redirectToCheckout } from "../lib/checkout";
import ReportDialog from "../components/ReportDialog";
import PaginationBar from "../components/PaginationBar";
import LegalLinksCard from "../components/LegalLinksCard";
import MnsHardBlockBanner from "../components/MnsHardBlockBanner";

const fmtDT = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };

const VALID_MNS_TABS = new Set(["planning", "calendar", "bookings", "clients", "reviews", "crm", "wallet", "accounting"]);

export default function MnsDashboard() {
  const [search, setSearch] = useSearchParams();
  const initialTab = (() => {
    const t = search.get("tab");
    return t && VALID_MNS_TABS.has(t) ? t : "planning";
  })();
  const [tab, setTab] = useState(initialTab);
  const [premium, setPremium] = useState(null);
  const [presetDate, setPresetDate] = useState(""); // YYYY-MM-DD prefill for PlanningTab
  // Iter35 patch4: deep-link to a specific client's pedagogical dialog
  const focusClientId = search.get("client") || "";

  // React to ?tab= changes (back/forward, or external links)
  useEffect(() => {
    const t = search.get("tab");
    if (t && VALID_MNS_TABS.has(t) && t !== tab) setTab(t);
    // eslint-disable-next-line
  }, [search]);

  // Reflect tab changes into the URL (without polluting history)
  const onTabChange = (next) => {
    setTab(next);
    const sp = new URLSearchParams(search);
    if (next === "planning") sp.delete("tab"); else sp.set("tab", next);
    // Drop client= if leaving the Clients tab
    if (next !== "clients") sp.delete("client");
    setSearch(sp, { replace: true });
  };

  useEffect(() => {
    api.get("/subscriptions/premium-status").then((r) => setPremium(r.data)).catch(() => {});
  }, []);

  const goAddSlotForDay = (isoDay) => {
    setPresetDate(isoDay);
    onTabChange("planning");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Espace Maître-Nageur</h1>
      {/* Iter35: trial countdown — only when in 90-day free Premium trial */}
      {premium && premium.source === "trial" && premium.active && (
        <div className="mb-5 rounded-2xl p-4 border-2 border-sky-300 bg-gradient-to-r from-sky-50 via-cyan-50 to-sky-50 flex items-center justify-between gap-3 flex-wrap"
             data-testid="mns-trial-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-sky-900" style={{ fontFamily: "Outfit" }}>
                Essai Premium gratuit — il vous reste {premium.trial_days_left} jour{premium.trial_days_left > 1 ? "s" : ""}
              </div>
              <div className="text-sm text-sky-800">
                CRM, Statistiques, Comptabilité et onglet Clients sont accessibles jusqu'au {premium.trial_ends_at ? new Date(premium.trial_ends_at).toLocaleDateString("fr-FR") : ""}.
              </div>
            </div>
          </div>
          <Link to="/profile?tab=subscription"><Button className="rounded-full bg-sky-700 hover:bg-sky-600 text-white" data-testid="mns-trial-cta">Voir les formules →</Button></Link>
        </div>
      )}
      {/* Iter35: out-of-trial / expired sub — outils Premium bloqués */}
      {premium && !premium.active && premium.trial_starts_at && (
        <div className="mb-5 rounded-2xl p-4 border-2 border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 flex items-center justify-between gap-3 flex-wrap"
             data-testid="mns-premium-expired-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-amber-900" style={{ fontFamily: "Outfit" }}>Votre essai Premium est terminé</div>
              <div className="text-sm text-amber-800">
                Vos créneaux et la messagerie restent actifs. Pour réactiver CRM / Statistiques / Comptabilité / Clients, souscrivez à un abonnement.
              </div>
            </div>
          </div>
          <Link to="/profile?tab=subscription"><Button className="rounded-full bg-amber-600 hover:bg-amber-500 text-white" data-testid="mns-premium-cta">S'abonner →</Button></Link>
        </div>
      )}
      <MnsHardBlockBanner />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="tabs-scroll bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="planning" data-testid="tab-planning">Créneaux</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Calendrier</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Réservations</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Avis</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet">Cagnotte</TabsTrigger>
          <TabsTrigger value="crm" data-testid="tab-crm">CRM & Statistiques</TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
          <TabsTrigger value="accounting" data-testid="tab-accounting">Comptabilité</TabsTrigger>
        </TabsList>
        <TabsContent value="planning"><PlanningTab presetDate={presetDate} clearPreset={() => setPresetDate("")} /></TabsContent>
        <TabsContent value="calendar"><CalendarTab onAddSlotForDay={goAddSlotForDay} /></TabsContent>
        <TabsContent value="bookings"><BookingsTab /></TabsContent>
        <TabsContent value="clients"><ClientsTab focusClientId={focusClientId} clearFocus={() => {
          const sp = new URLSearchParams(search); sp.delete("client"); setSearch(sp, { replace: true });
        }} /></TabsContent>
        <TabsContent value="reviews"><ReviewsTab /></TabsContent>
        <TabsContent value="crm"><CrmTab /></TabsContent>
        <TabsContent value="wallet"><WalletTab /></TabsContent>
        <TabsContent value="accounting"><AccountingTab /></TabsContent>
      </Tabs>

      <div className="mt-8">
        <LegalLinksCard />
      </div>
    </div>
  );
}

function PlanningTab({ presetDate = "", clearPreset = () => {} }) {
  const [pools, setPools] = useState([]);
  const [avails, setAvails] = useState([]);
  const [form, setForm] = useState({ pool_id: "", date: "", start: "09:00", end: "10:00", max_students: 1, accepts_mixed_levels: false, price: "" });
  const [profile, setProfile] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));

  // Pre-fill the date from CalendarTab and scroll to the form
  useEffect(() => {
    if (presetDate) {
      setForm((f) => ({ ...f, date: presetDate }));
      // smooth scroll + tiny highlight
      setTimeout(() => {
        const el = document.querySelector('[data-testid="planning-add-form"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      // Reset preset so it doesn't stick if user revisits
      setTimeout(() => clearPreset(), 1500);
    }
  }, [presetDate, clearPreset]);

  const reload = async () => {
    const { data: prof } = await api.get("/profile/mns");
    setProfile(prof);
    if (prof?.attached_pool_ids?.length) {
      const { data: pl } = await api.get("/pools");
      setPools(pl.filter((p) => prof.attached_pool_ids.includes(p.pool_id)));
    }
    const { data: av } = await api.get("/availabilities", { params: { mns_id: (await api.get("/auth/me")).data.user_id } });
    setAvails(av);
  };
  useEffect(() => { reload(); }, []);

  const computeDefaultPrice = () => {
    if (!profile?.hourly_rate) return "";
    const [h1, m1] = form.start.split(":").map(Number);
    const [h2, m2] = form.end.split(":").map(Number);
    const hours = Math.max(0.5, (h2 * 60 + m2 - h1 * 60 - m1) / 60);
    return (profile.hourly_rate * hours).toFixed(2);
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.pool_id) return toast.error("Sélectionnez un établissement");
    const start = new Date(`${form.date}T${form.start}:00`).toISOString();
    const end = new Date(`${form.date}T${form.end}:00`).toISOString();
    const priceVal = form.price === "" || form.price == null ? null : parseFloat(form.price);
    try {
      await api.post("/availabilities", { ...form, start_dt: start, end_dt: end, price: priceVal });
      toast.success("Créneau créé");
      setForm({ ...form, price: "" });
      reload();
    } catch (err) {
      const detail = err.response?.data?.detail || "Création impossible";
      toast.error(detail, { duration: 7000 });
    }
  };
  const [duplicateDialog, setDuplicateDialog] = useState(null); // {conflicts, src, target}
  const [deleteDialog, setDeleteDialog] = useState(null); // {avail_id, bookings:[...]}
  // Iter49 — edit slot dialog
  const [editSlot, setEditSlot] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const openEditSlot = (a) => {
    const s = new Date(a.start_dt);
    const e = new Date(a.end_dt);
    const pad = (n) => String(n).padStart(2, "0");
    setEditSlot({
      avail_id: a.avail_id,
      pool_id: a.pool_id || "",
      date: `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`,
      start: `${pad(s.getHours())}:${pad(s.getMinutes())}`,
      end: `${pad(e.getHours())}:${pad(e.getMinutes())}`,
      max_students: a.max_students || 1,
      price: a.price ?? "",
      age_group: a.age_group || "adult",
      level: a.level || "1",
      accepts_mixed_levels: !!a.accepts_mixed_levels,
      hidden_by_mns: !!a.hidden_by_mns,
      _prev_max: a.max_students || 1,
      _prev_date: `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`,
      _prev_start: `${pad(s.getHours())}:${pad(s.getMinutes())}`,
      _prev_end: `${pad(e.getHours())}:${pad(e.getMinutes())}`,
      _prev_price: a.price ?? "",
      _prev_age: a.age_group || "adult",
      _prev_level: a.level || "1",
      _prev_mixed: !!a.accepts_mixed_levels,
      _booked: a.booked_count || 0,
    });
  };
  const saveEditSlot = async () => {
    if (!editSlot) return;
    setEditSaving(true);
    try {
      const startISO = new Date(`${editSlot.date}T${editSlot.start}:00`).toISOString();
      const endISO = new Date(`${editSlot.date}T${editSlot.end}:00`).toISOString();
      const priceVal = editSlot.price === "" || editSlot.price == null ? null : parseFloat(editSlot.price);
      const payload = {
        pool_id: editSlot.pool_id,
        start_dt: startISO,
        end_dt: endISO,
        max_students: editSlot.max_students,
        price: priceVal,
        age_group: editSlot.age_group,
        level: editSlot.level,
        accepts_mixed_levels: editSlot.accepts_mixed_levels,
        hidden_by_mns: editSlot.hidden_by_mns,
      };
      const { data } = await api.put(`/availabilities/${editSlot.avail_id}`, payload);
      if (data.unchanged) {
        toast.info("Aucune modification");
      } else if (data.notified > 0) {
        toast.success(`Créneau modifié — ${data.notified} client(s) notifié(s).`);
      } else {
        toast.success("Créneau modifié");
      }
      setEditSlot(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Modification impossible");
    } finally {
      setEditSaving(false);
    }
  };

  const del = async (id, confirm = false) => {
    try {
      await api.delete(`/availabilities/${id}`, { params: { confirm } });
      toast.success(confirm ? "Créneau supprimé · clients notifiés · remboursements crédités" : "Supprimé");
      setDeleteDialog(null);
      reload();
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data?.detail;
      if (status === 409 && data && typeof data === "object" && data.code === "has_active_bookings") {
        setDeleteDialog({ avail_id: id, bookings: data.bookings, message: data.message });
        return;
      }
      toast.error(typeof data === "string" ? data : "Suppression impossible");
    }
  };
  const dup = async (force = false) => {
    // Iter40 quater: force must be a real boolean (onClick passes SyntheticEvent — we
    // now wrap the handler at the call site, but stay defensive in case of regression).
    const forceFlag = force === true;
    const src = weekStart.toISOString();
    const target = new Date(weekStart.getTime() + 7 * 24 * 3600 * 1000).toISOString();
    try {
      const { data } = await api.post("/availabilities/duplicate-week", { source_week_start: src, target_week_start: target, force: forceFlag });
      if ((data.conflicts || []).length > 0 && !forceFlag) {
        setDuplicateDialog({ conflicts: data.conflicts, src, target, createdSoFar: data.created });
        return;
      }
      toast.success(`${data.created} créneau(x) créé(s)${data.replaced ? ` · ${data.replaced} remplacé(s)` : ""}`);
      setDuplicateDialog(null);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Duplication impossible"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    const meta = {};
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    // Iter46 fix: same off-by-one-day bug as PublicCalendar — use LOCAL date key.
    const localDayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    avails.forEach((a) => {
      const d = new Date(a.start_dt);
      // Exclude past slots (end time already passed) from the active planning view
      if (new Date(a.end_dt) < new Date()) return;
      const isoDay = localDayKey(d);
      const label = d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "short" });
      (g[isoDay] ||= []).push(a);
      meta[isoDay] = label;
    });
    // Sort each day's slots by start time
    Object.values(g).forEach((list) => list.sort((a, b) => a.start_dt.localeCompare(b.start_dt)));
    // Return array of [isoDay, label, list] sorted chronologically
    return Object.keys(g).sort().map((iso) => [iso, meta[iso], g[iso]]);
  }, [avails]);

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6 mt-6">
      <div className="card-soft p-6 h-fit" data-testid="planning-add-form">
        <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Nouveau créneau</h3>
        {!profile?.attached_pool_ids?.length && <a href="/mns/profile" className="block text-sm text-amber-700 bg-amber-50 p-2 rounded-lg mb-3">⚠ Complétez votre profil et choisissez un établissement →</a>}
        <form onSubmit={add} className="space-y-3">
          <div>
            <Label>Établissement</Label>
            <Select value={form.pool_id} onValueChange={(v) => setForm({ ...form, pool_id: v })}>
              <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent>{pools.map((p) => <SelectItem key={p.pool_id} value={p.pool_id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Début</Label><Input type="time" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
            <div><Label>Fin</Label><Input type="time" required value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Tarif du cours (€) <span className="text-xs text-slate-400">— obligatoire</span></Label>
              <Input type="number" min="0" step="0.5" required placeholder="ex: 45" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="avail-price" />
            </div>
            <div>
              <Label>Élèves max <span className="text-xs text-slate-400">— par défaut 1</span></Label>
              <Input type="number" min="1" step="1" required value={form.max_students} onChange={(e) => setForm({ ...form, max_students: Math.max(1, parseInt(e.target.value || "1", 10)) })} data-testid="avail-max-students" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-sky-700" checked={form.accepts_mixed_levels} onChange={(e) => setForm({ ...form, accepts_mixed_levels: e.target.checked })} data-testid="avail-mixed" />
            <span>Cours commun à plusieurs niveaux</span>
          </label>
          <p className="text-[11px] text-slate-500 -mt-1">Définissez le tarif du cours. Votre fiche publique affichera la fourchette de prix automatiquement.</p>
          <Button type="submit" className="w-full rounded-full bg-sky-700 hover:bg-sky-600" data-testid="add-avail-btn">Créer</Button>
        </form>

        <div className="mt-6 border-t pt-4">
          <Label>Dupliquer une semaine</Label>
          <Input type="date" value={weekStart.toISOString().slice(0, 10)} onChange={(e) => setWeekStart(new Date(e.target.value))} className="mt-1" />
          <Button onClick={() => dup(false)} variant="outline" className="w-full mt-2 rounded-full" data-testid="duplicate-week-btn"><Copy className="w-4 h-4 mr-1" />Dupliquer vers semaine suivante</Button>
        </div>
      </div>

      <div className="space-y-4">
        {grouped.length === 0 && <div className="card-soft p-8 text-center text-slate-500">Aucun créneau. Créez votre premier créneau →</div>}
        {grouped.map(([iso, label, list]) => (
          <div key={iso} className="card-soft p-5" data-testid={`planning-day-${iso}`}>
            <div className="font-semibold text-sky-800 mb-3 capitalize" style={{ fontFamily: "Outfit" }}>{label}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {list.map((a) => (
                <button
                  key={a.avail_id}
                  type="button"
                  onClick={() => openEditSlot(a)}
                  className="border rounded-xl p-3 flex items-center justify-between text-left hover:bg-sky-50 transition focus:outline-none focus:ring-2 focus:ring-sky-300"
                  data-testid={`planning-slot-${a.avail_id}`}
                  title="Cliquer pour modifier ce créneau"
                >
                  <div>
                    <div className="font-medium text-slate-900">{new Date(a.start_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} → {new Date(a.end_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="text-xs text-slate-500">{a.booked_count}/{a.max_students} réservés{a.price != null ? ` · ${a.price}€` : ""}</div>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); del(a.avail_id, false); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); del(a.avail_id, false); } }}
                    className="p-2 rounded-md hover:bg-red-50 cursor-pointer"
                    data-testid={`del-${a.avail_id}`}
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Iter39: Duplicate-week conflict resolution dialog */}
      <Dialog open={!!duplicateDialog} onOpenChange={(v) => !v && setDuplicateDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="duplicate-conflicts-dialog">
          <DialogHeader>
            <DialogTitle>Conflits détectés lors de la duplication</DialogTitle>
            <DialogDescription>
              {duplicateDialog?.createdSoFar} créneau(x) ont été créés sans conflit. Les créneaux suivants existent déjà
              sur la semaine cible. Choisissez s'il faut conserver l'existant ou le remplacer.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2">
            {(duplicateDialog?.conflicts || []).map((c) => (
              <div key={c.new_start_dt} className="border rounded-xl p-3 text-sm">
                <div className="font-medium">{new Date(c.new_start_dt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  <div className="bg-slate-50 rounded-lg p-2 text-xs">
                    <div className="font-semibold text-emerald-700">Existant (à conserver par défaut)</div>
                    <div>{new Date(c.existing_start_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} → {new Date(c.existing_end_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div>Tarif : {c.existing_price ?? "défaut"}€</div>
                    <div className={c.existing_bookings_count > 0 ? "text-amber-700 font-semibold" : "text-slate-500"}>
                      {c.existing_bookings_count > 0 ? `⚠ ${c.existing_bookings_count} réservation(s)` : "Aucune réservation"}
                    </div>
                  </div>
                  <div className="bg-sky-50 rounded-lg p-2 text-xs">
                    <div className="font-semibold text-sky-700">Nouveau (si vous remplacez)</div>
                    <div>{new Date(c.new_start_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} → {new Date(c.new_end_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div>Tarif : {c.new_price ?? "défaut"}€</div>
                  </div>
                </div>
                {c.existing_bookings_count > 0 && (
                  <div className="mt-2 text-xs text-amber-800 bg-amber-50 rounded p-2">
                    Ce créneau a des réservations actives — il sera <strong>conservé tel quel</strong> (pas de remplacement possible).
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDuplicateDialog(null)}>Conserver tout l'existant</Button>
            <Button className="rounded-full bg-sky-700 hover:bg-sky-600" onClick={() => dup(true)} data-testid="dup-force-btn">
              Remplacer (sauf ceux avec réservations)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter39: Slot deletion with active bookings confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={(v) => !v && setDeleteDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="delete-with-bookings-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">⚠ Ce créneau a des réservations actives</DialogTitle>
            <DialogDescription>{deleteDialog?.message}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[40vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr><th className="text-left p-2">Client</th><th className="text-left p-2">Paiement</th><th className="text-right p-2">Montant</th></tr>
              </thead>
              <tbody>
                {(deleteDialog?.bookings || []).map((b) => (
                  <tr key={b.booking_id} className="border-t">
                    <td className="p-2">{b.client_name}<div className="text-xs text-slate-400">{b.client_email}</div></td>
                    <td className="p-2">{b.payment_method === "online" ? (b.payment_status === "paid" ? "En ligne · payé → sera remboursé" : "En ligne · en attente") : "Espèces"}</td>
                    <td className="p-2 text-right font-medium">{(b.amount || 0).toFixed(2)}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDeleteDialog(null)}>Annuler</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700 text-white"
              onClick={() => del(deleteDialog.avail_id, true)} data-testid="delete-confirm-btn">
              Confirmer la suppression + rembourser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter49 — Edit slot dialog */}
      <Dialog open={!!editSlot} onOpenChange={(v) => { if (!v && !editSaving) setEditSlot(null); }}>
        <DialogContent data-testid="edit-slot-dialog">
          <DialogHeader>
            <DialogTitle>Modifier le créneau</DialogTitle>
            <DialogDescription className="text-xs">
              La modification écrase l'ancien créneau.
              {editSlot?._booked > 0 && (
                <span className="block mt-1 text-amber-700">
                  ⚠ {editSlot._booked} réservation{editSlot._booked > 1 ? "s" : ""} active{editSlot._booked > 1 ? "s" : ""}.
                  Les clients seront notifiés sauf si vous augmentez uniquement la capacité.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {editSlot && (() => {
            const changes = [];
            if (editSlot.date !== editSlot._prev_date) changes.push("date");
            if (editSlot.start !== editSlot._prev_start) changes.push("heure de début");
            if (editSlot.end !== editSlot._prev_end) changes.push("heure de fin");
            if (Number(editSlot.max_students) < Number(editSlot._prev_max)) changes.push("capacité réduite");
            if (parseFloat(editSlot.price || 0) !== parseFloat(editSlot._prev_price || 0)) changes.push("tarif");
            if (editSlot.age_group !== editSlot._prev_age) changes.push("tranche d'âge");
            if (editSlot.level !== editSlot._prev_level) changes.push("niveau");
            if (editSlot.accepts_mixed_levels !== editSlot._prev_mixed) changes.push("niveaux mixtes");
            const willNotify = changes.length > 0 && editSlot._booked > 0;
            return willNotify ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800 flex items-start gap-2" data-testid="edit-notify-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">{editSlot._booked} client{editSlot._booked > 1 ? "s" : ""} sera{editSlot._booked > 1 ? "nt" : ""} notifié{editSlot._booked > 1 ? "s" : ""}</div>
                  <div className="text-xs mt-0.5">Modifications détectées : {changes.join(", ")}.</div>
                </div>
              </div>
            ) : null;
          })()}
          {editSlot && (
            <div className="space-y-3">
              <div>
                <Label>Établissement</Label>
                <Select value={editSlot.pool_id} onValueChange={(v) => setEditSlot({ ...editSlot, pool_id: v })}>
                  <SelectTrigger data-testid="edit-pool"><SelectValue /></SelectTrigger>
                  <SelectContent>{pools.map((p) => <SelectItem key={p.pool_id} value={p.pool_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={editSlot.date} onChange={(e) => setEditSlot({ ...editSlot, date: e.target.value })} data-testid="edit-date" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Début</Label><Input type="time" value={editSlot.start} onChange={(e) => setEditSlot({ ...editSlot, start: e.target.value })} data-testid="edit-start" /></div>
                <div><Label>Fin</Label><Input type="time" value={editSlot.end} onChange={(e) => setEditSlot({ ...editSlot, end: e.target.value })} data-testid="edit-end" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Tarif (€)</Label>
                  <Input type="number" min="0" step="0.5" value={editSlot.price} onChange={(e) => setEditSlot({ ...editSlot, price: e.target.value })} data-testid="edit-price" />
                </div>
                <div>
                  <Label>Élèves max</Label>
                  <Input type="number" min="1" step="1" value={editSlot.max_students} onChange={(e) => setEditSlot({ ...editSlot, max_students: Math.max(1, parseInt(e.target.value || "1", 10)) })} data-testid="edit-max" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Tranche d'âge</Label>
                  <Select value={editSlot.age_group} onValueChange={(v) => setEditSlot({ ...editSlot, age_group: v })}>
                    <SelectTrigger data-testid="edit-age"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="child">Enfant</SelectItem>
                      <SelectItem value="adult">Adulte</SelectItem>
                      <SelectItem value="senior">Senior</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Niveau</Label>
                  <Select value={editSlot.level} onValueChange={(v) => setEditSlot({ ...editSlot, level: v })}>
                    <SelectTrigger data-testid="edit-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Débutant</SelectItem>
                      <SelectItem value="1">Intermédiaire</SelectItem>
                      <SelectItem value="2">Confirmé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-sky-700" checked={editSlot.accepts_mixed_levels} onChange={(e) => setEditSlot({ ...editSlot, accepts_mixed_levels: e.target.checked })} data-testid="edit-mixed" />
                <span>Cours commun à plusieurs niveaux</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-violet-700" checked={editSlot.hidden_by_mns} onChange={(e) => setEditSlot({ ...editSlot, hidden_by_mns: e.target.checked })} data-testid="edit-hidden" />
                <span>Cacher tant que les créneaux précédents ne sont pas réservés</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditSlot(null)} disabled={editSaving}>Annuler</Button>
            <Button className="rounded-full bg-sky-700 hover:bg-sky-600" onClick={saveEditSlot} disabled={editSaving} data-testid="edit-save-btn">
              {editSaving ? "Enregistrement…" : "Confirmer la modification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [mainTab, setMainTab] = useState("active");
  const [period, setPeriod] = useState("month"); // week | month | year
  const [anchor, setAnchor] = useState(new Date()); // reference date for history period
  const [clientDialog, setClientDialog] = useState(null);
  const [activePage, setActivePage] = useState(0);
  const [activeSize, setActiveSize] = useState(20);
  const [historyPage, setHistoryPage] = useState(0);
  const [historySize, setHistorySize] = useState(20);
  const nav = useNavigate();
  const confirm = useConfirm();
  const reload = () => api.get("/bookings/me").then((r) => setBookings(r.data));
  useEffect(() => { reload(); }, []);

  const isHistory = (s) => ["completed", "cancelled", "no_show"].includes(s);

  const active = useMemo(
    () => bookings.filter((b) => !isHistory(b.status))
      .sort((a, b) => a.start_dt.localeCompare(b.start_dt)),
    [bookings]
  );

  const inPeriod = (b) => {
    const d = new Date(b.start_dt);
    const ref = new Date(anchor);
    if (period === "week") {
      const ws = startOfWeek(ref);
      const we = new Date(ws.getTime() + 7 * 24 * 3600 * 1000);
      return d >= ws && d < we;
    }
    if (period === "month") {
      return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
    }
    return d.getFullYear() === ref.getFullYear();
  };

  const history = useMemo(
    () => bookings.filter((b) => isHistory(b.status) && inPeriod(b))
      .sort((a, b) => b.start_dt.localeCompare(a.start_dt)),
    // eslint-disable-next-line
    [bookings, period, anchor]
  );

  const act = async (b, action) => {
    const messages = {
      confirm: { title: "Valider cette réservation ?", body: `Le client recevra une notification de confirmation pour le cours du ${new Date(b.start_dt).toLocaleString("fr-FR")}.`, ok: "Valider" },
      complete: { title: "Valider la présence du client ?", body: "Cette action crédite votre cagnotte du tarif du cours (pour les paiements en ligne). À ne valider qu'après que le client ait suivi le cours.", ok: "Présence validée", tone: "info" },
      "no-show": { title: "Déclarer une absence (no-show) ?", body: "Le client sera marqué absent. Une pénalité de 30 % du tarif sera automatiquement facturée sur sa carte enregistrée (ou conservée si le cours a été réglé en ligne), dont 90 % reversés sur votre cagnotte.", ok: "Déclarer absent", tone: "warn" },
      cancel: { title: "Annuler ce créneau ?", body: "Cette action annulera la réservation pour le client. Elle sera notifiée.", ok: "Confirmer l'annulation", tone: "warn" },
      revert: { title: "Annuler cette action ?", body: "La réservation repassera en statut en attente / confirmée et toute pénalité ou crédit cagnotte lié sera annulé.", ok: "Annuler l'action", tone: "warn" },
    };
    const m = messages[action] || { title: "Confirmer", body: "" };
    const ok = await confirm({ title: m.title, body: m.body, confirmLabel: m.ok || "Valider", tone: m.tone || "info" });
    if (!ok) return;
    try {
      const { data } = await api.post(`/bookings/${b.booking_id}/${action}`);
      if (action === "no-show") {
        const pc = data?.penalty_charge;
        if (pc?.charged) {
          toast.success(`No-show enregistré — pénalité de ${pc.amount?.toFixed?.(2) || pc.amount}€ prélevée sur la carte du client. Votre cagnotte est créditée.`);
        } else if (pc?.refunded) {
          toast.success(`No-show enregistré — 30 % conservés sur le paiement initial (70 % remboursés au client). Votre cagnotte est créditée.`);
        } else if (pc && pc.error) {
          toast.warning(`No-show enregistré, mais la pénalité n'a pas pu être prélevée : ${pc.error}. Le client sera relancé.`);
        } else {
          toast.success("No-show enregistré");
        }
      } else {
        toast.success("Mise à jour");
      }
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Iter35 wave 2: cash-received confirmation
  const actCash = async (b) => {
    const ok = await confirm({
      title: "Confirmer la réception des espèces ?",
      body: `Vous attestez avoir bien reçu ${b.amount?.toFixed?.(2) || b.amount}€ en espèces du client ${b.client?.name} pour le cours du ${new Date(b.start_dt).toLocaleString("fr-FR")}.`,
      confirmLabel: "Oui, j'ai reçu les espèces",
      tone: "info",
    });
    if (!ok) return;
    try {
      await api.post(`/bookings/${b.booking_id}/cash-received`);
      toast.success("Réception des espèces enregistrée"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const shiftAnchor = (dir) => {
    const ref = new Date(anchor);
    if (period === "week") ref.setDate(ref.getDate() + 7 * dir);
    else if (period === "month") ref.setMonth(ref.getMonth() + dir);
    else ref.setFullYear(ref.getFullYear() + dir);
    setAnchor(ref);
  };

  const periodLabel = () => {
    const ref = new Date(anchor);
    if (period === "week") {
      const ws = startOfWeek(ref);
      const we = new Date(ws.getTime() + 6 * 24 * 3600 * 1000);
      return `Semaine du ${ws.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} au ${we.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}`;
    }
    if (period === "month") return ref.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return String(ref.getFullYear());
  };

  const StatusBadge = ({ s }) => {
    const map = {
      confirmed: ["bg-emerald-100 text-emerald-700", "Confirmée"],
      pending: ["bg-amber-100 text-amber-700", "En attente"],
      no_show: ["bg-red-100 text-red-700", "Absent"],
      cancelled: ["bg-slate-200 text-slate-600", "Annulée"],
      completed: ["bg-sky-100 text-sky-700", "Effectuée"],
    };
    const [cls, label] = map[s] || ["bg-slate-100 text-slate-600", s];
    return <span className={`text-xs px-2 py-1 rounded-full ${cls}`}>{label}</span>;
  };

  const BookingRow = ({ b, showActions }) => (
    <div className="card-soft p-5" data-testid={`bk-row-${b.booking_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold">{fmtDT(b.start_dt)}</div>
          <div className="text-sm text-slate-600">
            Client :{" "}
            {b.client?.user_id ? (
              <button
                type="button"
                onClick={() => setClientDialog(b.client)}
                className="text-sky-700 hover:underline font-medium"
                data-testid={`bk-client-link-${b.booking_id}`}
              >
                {b.client.name}
              </button>
            ) : (
              <span>{b.client?.name}</span>
            )}
            {" • "}{b.pool?.name}
          </div>
          <div className="mt-1 flex gap-2 flex-wrap">
            <StatusBadge s={b.status} />
            <span className="text-xs px-2 py-1 rounded-full bg-sky-100 text-sky-700">{b.payment_method === "online" ? "En ligne" : "Espèces"}</span>
            {b.payment_method === "cash" && b.cash_received_at && (
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800" title={`Encaissé le ${new Date(b.cash_received_at).toLocaleString("fr-FR")}`} data-testid={`cash-received-badge-${b.booking_id}`}>
                ✓ Espèces reçues
              </span>
            )}
            {b.penalty_amount ? <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">Pénalité {b.penalty_amount}€</span> : null}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {showActions ? (
            <>
              {b.status === "pending" && <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => act(b, "confirm")} data-testid={`confirm-${b.booking_id}`}><Check className="w-3 h-3 mr-1" />Valider</Button>}
              {b.status === "confirmed" && b.payment_method === "cash" && !b.cash_received_at && (
                <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-500 text-white"
                        onClick={() => actCash(b)} data-testid={`cash-received-${b.booking_id}`}
                        title="Confirmer la réception des espèces">
                  <Wallet className="w-3 h-3 mr-1" />J'ai reçu les espèces
                </Button>
              )}
              {b.status === "confirmed" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(b, "complete")} data-testid={`complete-${b.booking_id}`}><Check className="w-3 h-3 mr-1" />Présence validée</Button>}
              {(b.status === "confirmed" || b.status === "pending") && <Button size="sm" variant="outline" className="rounded-full text-amber-700 border-amber-300" onClick={() => act(b, "no-show")} data-testid={`noshow-${b.booking_id}`}><AlertTriangle className="w-3 h-3 mr-1" />No-show</Button>}
              {(b.status === "confirmed" || b.status === "pending") && <Button size="sm" variant="ghost" className="rounded-full text-slate-600" onClick={() => act(b, "cancel")} data-testid={`cancel-${b.booking_id}`}><X className="w-3 h-3 mr-1" />Annuler</Button>}
            </>
          ) : (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(b, "revert")} data-testid={`revert-${b.booking_id}`} title="Annuler cette action">
              <CornerUpLeft className="w-3 h-3 mr-1" />Annuler l'action
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="mt-6" data-testid="mns-bookings">
      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl mb-4">
          <TabsTrigger value="active" data-testid="bk-tab-active">Actives ({active.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="bk-tab-history">Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="active">
          <div className="grid gap-3">
            {active.length === 0 && <div className="card-soft p-8 text-center text-slate-500">Aucune réservation active.</div>}
            {active.slice(activePage * activeSize, (activePage + 1) * activeSize).map((b) => <BookingRow key={b.booking_id} b={b} showActions={true} />)}
          </div>
          <PaginationBar
            page={activePage} setPage={setActivePage}
            totalPages={Math.max(1, Math.ceil(active.length / activeSize))}
            pageSize={activeSize} setPageSize={(v) => { setActiveSize(v); setActivePage(0); }}
            total={active.length} testId="bk-active-pager"
          />
        </TabsContent>
        <TabsContent value="history">
          <div className="flex flex-wrap items-center gap-2 mb-4 card-soft p-3">
            <Label className="text-xs">Tri</Label>
            <select className="border rounded-lg px-2 py-1 text-sm" value={period} onChange={(e) => { setPeriod(e.target.value); setHistoryPage(0); }} data-testid="bk-history-period">
              <option value="week">Par semaine</option>
              <option value="month">Par mois</option>
              <option value="year">Par année</option>
            </select>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { shiftAnchor(-1); setHistoryPage(0); }} data-testid="bk-history-prev">← Précédent</Button>
            <span className="text-sm text-slate-700 font-medium" data-testid="bk-history-label">{periodLabel()}</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => { shiftAnchor(1); setHistoryPage(0); }} data-testid="bk-history-next">Suivant →</Button>
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setAnchor(new Date())}>Aujourd'hui</Button>
            <span className="ml-auto text-sm text-slate-500">{history.length} entrée(s)</span>
          </div>
          <div className="grid gap-3">
            {history.length === 0 && <div className="card-soft p-8 text-center text-slate-500">Aucune réservation dans cette période.</div>}
            {history.slice(historyPage * historySize, (historyPage + 1) * historySize).map((b) => <BookingRow key={b.booking_id} b={b} showActions={false} />)}
          </div>
          <PaginationBar
            page={historyPage} setPage={setHistoryPage}
            totalPages={Math.max(1, Math.ceil(history.length / historySize))}
            pageSize={historySize} setPageSize={(v) => { setHistorySize(v); setHistoryPage(0); }}
            total={history.length} testId="bk-history-pager"
          />
        </TabsContent>
      </Tabs>

      <Dialog open={!!clientDialog} onOpenChange={(v) => !v && setClientDialog(null)}>
        <DialogContent data-testid="client-info-dialog">
          <DialogHeader>
            <DialogTitle>Fiche client</DialogTitle>
          </DialogHeader>
          {clientDialog && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img
                  src={clientDialog.picture
                    ? (clientDialog.picture.startsWith("http") ? clientDialog.picture : `${process.env.REACT_APP_BACKEND_URL}${clientDialog.picture}`)
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(clientDialog.name)}&background=0369A1&color=fff&size=200`}
                  alt=""
                  className="w-16 h-16 rounded-full ring-2 ring-sky-200 object-cover"
                />
                <div>
                  <div className="font-semibold text-slate-900">{clientDialog.name}</div>
                  <div className="text-xs text-slate-500">Client</div>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  className="rounded-full bg-sky-700 hover:bg-sky-600"
                  onClick={() => { const id = clientDialog.user_id; setClientDialog(null); nav(`/messages/${id}`); }}
                  data-testid="client-info-message-btn"
                >
                  Envoyer un message
                </Button>
                <ReportDialog
                  targetType="user"
                  targetId={clientDialog.user_id}
                  targetLabel={clientDialog.name}
                  size="sm"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------------- Iter35 wave 3: Clients tab (Premium) -----------------------
function ClientsTab({ focusClientId = "", clearFocus = () => {} }) {
  const [data, setData] = useState({ items: [], total: 0, page: 0, page_size: 20 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error403, setError403] = useState(false);
  const [selected, setSelected] = useState(null); // client object opened in side dialog

  const reload = () => {
    setLoading(true); setError403(false);
    api.get("/mns-clients", { params: { page, page_size: pageSize, q } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setError403(true); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [page, pageSize]);
  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); reload(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Iter35 patch4: deep-link — open the dialog on a specific client when ?client=<id>
  // is in the URL. We fetch the public-card-ish info even if the client isn't on
  // the current page so the dialog opens immediately.
  useEffect(() => {
    if (!focusClientId) return;
    const found = data.items.find((c) => c.user_id === focusClientId);
    if (found) { setSelected(found); return; }
    // Lookup minimal info via the public-card endpoint
    api.get(`/users/${focusClientId}/public-card`).then((r) => {
      const u = r.data;
      setSelected({
        user_id: u.user_id,
        name: u.name,
        email: "",  // hidden by privacy
        picture: u.picture,
        bookings_count: u.bookings_with_you ?? 0,
        last_booking: null,
      });
    }).catch(() => {
      toast.error("Élève introuvable");
      clearFocus();
    });
    // eslint-disable-next-line
  }, [focusClientId, data.items.length]);

  if (error403) return <PremiumPaywall feature="L'onglet Clients" />;

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div className="space-y-4" data-testid="clients-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Users className="w-5 h-5 text-sky-700" />
          Mes clients <span className="text-sm font-normal text-slate-500">({data.total})</span>
        </h3>
        <Input
          placeholder="Rechercher un client (nom, email)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
          data-testid="clients-search"
        />
      </div>

      {loading ? (
        <div className="card-soft p-8 text-center text-slate-500">Chargement…</div>
      ) : data.items.length === 0 ? (
        <div className="card-soft p-12 text-center text-slate-500" data-testid="clients-empty">
          {q ? `Aucun résultat pour "${q}"` : "Aucun client pour l'instant. Vos clients apparaîtront ici dès leur 1ère réservation."}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.items.map((c) => (
              <button
                key={c.user_id}
                type="button"
                onClick={() => setSelected(c)}
                className="card-soft p-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all"
                data-testid={`client-card-${c.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={c.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(c.name)}&background=0369A1&color=fff`}
                    alt="" className="w-12 h-12 rounded-full ring-2 ring-sky-100"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.email}</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-sky-50 rounded-lg px-2 py-1.5 text-sky-800">
                    <div className="font-bold text-base">{c.bookings_count}</div>
                    <div className="text-[10px] uppercase tracking-wide">cours</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-2 py-1.5 text-slate-700">
                    <div className="text-[11px]">Dernier</div>
                    <div className="font-medium truncate">{c.last_booking ? new Date(c.last_booking).toLocaleDateString("fr-FR") : "—"}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-3 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Par page</Label>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                className="border border-slate-200 rounded-md px-2 py-1 text-sm"
                data-testid="clients-page-size"
              >
                {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline" size="sm" className="rounded-full"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                data-testid="clients-page-prev"
              >← Précédent</Button>
              <span className="text-slate-500 text-xs px-2">Page {page + 1} / {totalPages}</span>
              <Button
                variant="outline" size="sm" className="rounded-full"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                data-testid="clients-page-next"
              >Suivant →</Button>
            </div>
          </div>
        </>
      )}

      {selected && <ClientPedagogicalDialog client={selected} onClose={() => { setSelected(null); clearFocus(); }} />}
    </div>
  );
}

function ClientPedagogicalDialog({ client, onClose }) {
  const [data, setData] = useState({ notes: [], bookings: [] });
  const [text, setText] = useState("");
  const [lessonDate, setLessonDate] = useState(new Date().toISOString().slice(0, 10));
  const [shareNew, setShareNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = () => api.get(`/mns-clients/${client.user_id}/notes`).then((r) => setData(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [client.user_id]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim()) { toast.error("Saisissez une observation"); return; }
    setBusy(true);
    try {
      await api.post(`/mns-clients/${client.user_id}/notes`, {
        text, lesson_date: lessonDate, share_with_client: shareNew,
      });
      setText(""); setShareNew(false);
      toast.success(shareNew ? "Note ajoutée et partagée avec le client" : "Note ajoutée");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggleShare = async (note) => {
    try {
      await api.patch(`/mns-clients/${client.user_id}/notes/${note.note_id}/share`, {
        share_with_client: !note.share_with_client,
      });
      toast.success(!note.share_with_client ? "Partagé avec le client" : "Note redevenue privée");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const uploadPhoto = async (note_id, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mns-clients/${client.user_id}/notes/${note_id}/photo`, {
        method: "POST", body: fd, credentials: "include",
        headers: csrfHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Photo ajoutée");
      reload();
    } catch (e) { toast.error("Échec du téléversement"); }
  };

  const remove = async (note_id) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await api.delete(`/mns-clients/${client.user_id}/notes/${note_id}`);
      toast.success("Supprimée"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div
      className="fixed inset-0 z-[1500] bg-black/40 flex items-stretch sm:items-center justify-center"
      onClick={onClose}
      data-testid="client-ped-dialog"
    >
      <div
        className="bg-white rounded-2xl sm:max-w-2xl w-full sm:m-4 m-0 max-h-[100vh] sm:max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b p-4 flex items-center gap-3 z-10">
          <img src={client.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=0369A1&color=fff`} alt="" className="w-10 h-10 rounded-full ring-2 ring-sky-100" />
          <div className="flex-1 min-w-0">
            <div className="font-bold truncate">{client.name}</div>
            <div className="text-xs text-slate-500 truncate">{client.email} · {client.bookings_count} cours</div>
          </div>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={onClose} data-testid="client-ped-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            🔒 Suivi pédagogique <strong>privé par défaut</strong> — vous pouvez choisir, note par note, de partager avec le client. Les notes partagées apparaissent dans son carnet de progression.
          </div>

          {/* Add note form */}
          <form onSubmit={submit} className="card-soft p-4 space-y-2 bg-sky-50/40" data-testid="ped-note-form">
            <Label className="text-sm">Nouvelle observation</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={lessonDate}
                onChange={(e) => setLessonDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="max-w-[160px]"
                data-testid="ped-note-date"
              />
              <span className="text-xs text-slate-500">Date de la leçon</span>
            </div>
            <textarea
              rows={3}
              className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
              placeholder="Ex: Travail sur la respiration crawl. Difficulté à expirer dans l'eau, exercice avec bouchons. Bons progrès sur les jambes."
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="ped-note-text"
            />
            <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="ped-note-share-label">
              <input
                type="checkbox"
                checked={shareNew}
                onChange={(e) => setShareNew(e.target.checked)}
                className="w-4 h-4 accent-sky-700"
                data-testid="ped-note-share-toggle"
              />
              <span>📖 Partager cette note avec le client (carnet de progression)</span>
            </label>
            <Button type="submit" disabled={busy} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="ped-note-submit">
              {busy ? "Envoi…" : "Enregistrer la note"}
            </Button>
          </form>

          {/* Existing notes */}
          <div>
            <h4 className="font-semibold text-slate-700 text-sm mb-2">Historique pédagogique ({data.notes.length})</h4>
            {data.notes.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6 border-2 border-dashed border-slate-200 rounded-xl">
                Aucune note pour l'instant. Ajoutez la première ci-dessus.
              </div>
            ) : (
              <div className="space-y-2">
                {data.notes.map((n) => (
                  <div key={n.note_id} className="card-soft p-3" data-testid={`ped-note-${n.note_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                          <span>Leçon du {new Date(n.lesson_date).toLocaleDateString("fr-FR")}</span>
                          <span>· ajoutée le {new Date(n.created_at).toLocaleString("fr-FR")}</span>
                          {n.share_with_client && (
                            <span className="bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 text-[10px] font-semibold" data-testid={`ped-note-shared-badge-${n.note_id}`}>
                              📖 Partagée avec le client
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{n.text}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost" size="sm"
                          className={`rounded-full ${n.share_with_client ? "text-emerald-700" : "text-slate-500"}`}
                          onClick={() => toggleShare(n)}
                          title={n.share_with_client ? "Rendre privée" : "Partager avec le client"}
                          data-testid={`ped-note-share-${n.note_id}`}
                        >
                          {n.share_with_client ? "🔓" : "🔒"}
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-full text-red-600" onClick={() => remove(n.note_id)} data-testid={`ped-note-del-${n.note_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {n.photos && n.photos.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {n.photos.map((src) => (
                          <a key={src} href={`${process.env.REACT_APP_BACKEND_URL}${src}`} target="_blank" rel="noreferrer">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${src}`} alt="" className="w-20 h-20 rounded-lg object-cover ring-1 ring-slate-200 hover:ring-sky-400 transition" />
                          </a>
                        ))}
                      </div>
                    )}
                    <label className="inline-flex items-center gap-1 cursor-pointer text-xs text-sky-700 font-medium hover:underline mt-2">
                      <Camera className="w-3.5 h-3.5" />Ajouter une photo
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                        onChange={(e) => uploadPhoto(n.note_id, e.target.files?.[0])}
                        data-testid={`ped-note-photo-${n.note_id}`}
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



function PremiumPaywall({ feature = "Cette fonctionnalité" }) {
  return (
    <div className="card-soft p-8 text-center max-w-xl mx-auto mt-6" data-testid="premium-paywall">
      <Crown className="w-10 h-10 text-amber-500 mx-auto mb-2" />
      <h3 className="font-bold text-slate-900 text-lg" style={{ fontFamily: "Outfit" }}>
        {feature} est réservée aux abonnés Premium
      </h3>
      <p className="text-slate-600 text-sm mt-1">
        Inclus dans votre essai gratuit de 3 mois (déclenché à votre 1ère réservation payée) et dans tous les abonnements.
      </p>
      <Link to="/profile?tab=subscription">
        <Button className="rounded-full bg-sky-700 hover:bg-sky-600 mt-4" data-testid="paywall-cta">
          Voir les formules →
        </Button>
      </Link>
    </div>
  );
}

function CrmTab() {
  const [period, setPeriod] = useState("month");
  const [stats, setStats] = useState(null);
  const [error403, setError403] = useState(false);
  useEffect(() => {
    setError403(false);
    api.get("/crm/stats", { params: { period } })
      .then((r) => setStats(r.data))
      .catch((e) => { if (e.response?.status === 403) setError403(true); });
  }, [period]);

  if (error403) return <PremiumPaywall feature="La rubrique CRM & Statistiques" />;
  if (!stats) return <div className="p-12 text-center text-slate-500">Chargement...</div>;

  const pieData = [
    { name: "En ligne", value: stats.ca_online },
    { name: "Espèces", value: stats.ca_cash },
  ];
  const COLORS = ["#0369A1", "#0D9488"];

  const Card = ({ icon: Icon, label, value, sub }) => (
    <div className="card-soft p-5">
      <div className="flex items-center justify-between"><Icon className="w-5 h-5 text-sky-700" /><span className="text-xs uppercase tracking-widest text-slate-500">{label}</span></div>
      <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="mt-6">
      <div className="flex items-center justify-end mb-4">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[200px] rounded-full" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Aujourd'hui</SelectItem>
            <SelectItem value="week">Cette semaine</SelectItem>
            <SelectItem value="month">Ce mois</SelectItem>
            <SelectItem value="quarter">Ce trimestre</SelectItem>
            <SelectItem value="year">Cette année</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Wallet} label="CA total" value={`${stats.ca_total}€`} sub={`En ligne ${stats.ca_online}€ • Espèces ${stats.ca_cash}€`} />
        <Card icon={Clock} label="Heures" value={stats.hours.toFixed(1)} sub="Heures réalisées" />
        <Card icon={Users} label="Clients" value={stats.clients} sub="Clients uniques" />
        <Card icon={CalIcon} label="Cours" value={stats.bookings_count} sub="Réservations" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="card-soft p-5">
          <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Répartition des paiements</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={90} label>
                {pieData.map((entry, i) => <Cell key={entry.name || `slice-${i}`} fill={COLORS[i]} />)}
              </Pie>
              <Legend /><RTooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="card-soft p-5">
          <h3 className="font-semibold mb-4" style={{ fontFamily: "Outfit" }}>Indicateurs clés</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[{ name: "Performance", CA: stats.ca_total, Heures: stats.hours, Clients: stats.clients, Cours: stats.bookings_count }]}>
              <XAxis dataKey="name" /><YAxis /><RTooltip />
              <Bar dataKey="CA" fill="#0369A1" /><Bar dataKey="Heures" fill="#0D9488" /><Bar dataKey="Clients" fill="#F59E0B" /><Bar dataKey="Cours" fill="#9333EA" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}


function CalendarTab({ onAddSlotForDay = () => {} }) {
  const [avails, setAvails] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; });
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedAvails, setSelectedAvails] = useState(new Set());
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelForm, setCancelForm] = useState({ reason_code: "technical", custom_text: "" });
  const [canceling, setCanceling] = useState(false);

  const reload = async () => {
    const me = (await api.get("/auth/me")).data;
    const from = new Date(cursor); from.setDate(1);
    const to = new Date(cursor); to.setMonth(to.getMonth() + 1); to.setDate(0); to.setHours(23,59,59,999);
    const { data: av } = await api.get("/availabilities", {
      params: { mns_id: me.user_id, from_dt: from.toISOString(), to_dt: to.toISOString() },
    });
    setAvails(av.filter((a) => !a.cancelled));
    const { data: bk } = await api.get("/bookings/me");
    setBookings(bk);
    setSelectedAvails(new Set());
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cursor]);

  const toggleSelect = (avail_id) => {
    const next = new Set(selectedAvails);
    if (next.has(avail_id)) next.delete(avail_id); else next.add(avail_id);
    setSelectedAvails(next);
  };

  const [confirmedToCancel, setConfirmedToCancel] = useState(new Set()); // ids ticked in the recap popup

  const openCancelDialog = () => {
    // Pre-tick all selected slots; user can untick any before confirming.
    setConfirmedToCancel(new Set(selectedAvails));
    setCancelOpen(true);
  };
  const toggleConfirmCancel = (avail_id) => {
    const next = new Set(confirmedToCancel);
    if (next.has(avail_id)) next.delete(avail_id); else next.add(avail_id);
    setConfirmedToCancel(next);
  };

  const doCancel = async () => {
    if (confirmedToCancel.size === 0) return toast.error("Aucun créneau coché à annuler");
    if (cancelForm.reason_code === "other" && !cancelForm.custom_text.trim()) {
      return toast.error("Veuillez préciser le motif dans le message personnalisé");
    }
    const ids = Array.from(confirmedToCancel);
    const payload = { avail_ids: ids, reason_code: cancelForm.reason_code, custom_text: cancelForm.custom_text };
    // Optimistic UI: close dialog + remove cancelled slots immediately
    setCancelOpen(false);
    setCancelForm({ reason_code: "technical", custom_text: "" });
    setSelectedAvails(new Set());
    setConfirmedToCancel(new Set());
    setAvails((prev) => prev.filter((a) => !ids.includes(a.avail_id)));
    setCanceling(true);
    try {
      const { data } = await api.post("/availabilities/cancel", payload);
      toast.success(`${data.availabilities_cancelled} créneau(x) annulé(s) • ${data.clients_notified} client(s) notifié(s)`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      // Roll back: refetch from server
      reload();
    } finally { setCanceling(false); }
  };

  // Iter43 — toggle progressive-reveal visibility of a single slot
  const toggleHidden = async (avail_id, currentHidden) => {
    try {
      await api.put(`/availabilities/${avail_id}/visibility`, { hidden_by_mns: !currentHidden });
      setAvails((prev) => prev.map((a) => a.avail_id === avail_id ? { ...a, hidden_by_mns: !currentHidden } : a));
      toast.success(!currentHidden
        ? "Créneau caché — il apparaîtra automatiquement quand les précédents seront réservés"
        : "Créneau rendu visible aux clients");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Build day map: date string -> { availList, totalCap, totalBooked }
  const dayMap = useMemo(() => {
    const m = {};
    avails.forEach((a) => {
      const d = new Date(a.start_dt);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
      if (!m[key]) m[key] = { availList: [], totalCap: 0, totalBooked: 0 };
      m[key].availList.push(a);
      m[key].totalCap += a.max_students;
      m[key].totalBooked += a.booked_count;
    });
    return m;
  }, [avails]);

  const monthDays = useMemo(() => {
    const first = new Date(cursor); first.setDate(1);
    const startWeekday = (first.getDay() + 6) % 7; // Mon=0
    const last = new Date(cursor); last.setMonth(last.getMonth() + 1); last.setDate(0);
    const days = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      const dt = new Date(cursor); dt.setDate(d);
      const key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
      days.push({ day: d, key, info: dayMap[key] });
    }
    return days;
  }, [cursor, dayMap]);

  const slotColor = (a) => {
    if (a.booked_count === 0) return "bg-slate-200 text-slate-600 border-slate-300";  // grey = empty
    if (a.booked_count >= a.max_students) return "bg-emerald-100 text-emerald-700 border-emerald-300"; // green = full
    return "bg-amber-100 text-amber-700 border-amber-300"; // yellow = partial
  };

  const dayBg = (info) => {
    if (!info || info.totalCap === 0) return "bg-white";
    if (info.totalBooked === 0) return "bg-slate-50";
    if (info.totalBooked >= info.totalCap) return "bg-emerald-50";
    return "bg-amber-50";
  };

  const monthLabel = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const prev = () => { const d = new Date(cursor); d.setMonth(d.getMonth() - 1); setCursor(d); setSelectedDay(null); };
  const next = () => { const d = new Date(cursor); d.setMonth(d.getMonth() + 1); setCursor(d); setSelectedDay(null); };

  const slotBookings = (avail_id) => bookings.filter((b) => b.avail_id === avail_id);

  return (
    <div className="mt-6" data-testid="mns-calendar">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={prev} data-testid="cal-prev">←</Button>
          <h3 className="text-xl font-semibold capitalize" style={{ fontFamily: "Outfit" }}>{monthLabel}</h3>
          <Button variant="outline" size="sm" className="rounded-full" onClick={next} data-testid="cal-next">→</Button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full border-sky-200 text-sky-700 hover:bg-sky-50"
            onClick={() => {
              const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
              window.location.href = `${apiBase}/api/bookings/me/calendar.ics`;
            }}
            data-testid="mns-cal-sync"
            title="Télécharger tous vos cours .ics (Google/Apple/Outlook). Rappels J-24h et H-1 inclus."
          >
            <Download className="w-4 h-4 mr-1" />Ajouter à mon agenda
          </Button>
          <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-slate-200 border border-slate-300" /> Vide</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300" /> Incomplet</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Complet</span>
          </div>
        </div>
      </div>
      <div className="card-soft p-3">
        <div className="grid grid-cols-7 gap-1 text-xs uppercase tracking-widest text-slate-500 mb-2">
          {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map((d) => <div key={d} className="text-center py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {monthDays.map((d, i) => d === null ? <div key={`empty-${i}`} /> : (
            <button
              key={d.key}
              onClick={() => {
                setSelectedDay(d.key);
                // Iter35 wave 2: jump straight to the day detail without scrolling
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="cal-day-detail"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 30);
              }}
              data-testid={`cal-day-${d.day}`}
              className={`min-h-[80px] border rounded-lg p-1.5 text-left transition ${dayBg(d.info)} ${selectedDay === d.key ? "ring-2 ring-sky-500" : "hover:border-sky-400"} cursor-pointer`}
            >
              <div className="text-xs font-semibold text-slate-700">{d.day}</div>
              {d.info && (
                <div className="mt-1 space-y-1">
                  {d.info.availList.slice(0, 2).map((a) => (
                    <div key={a.avail_id} className={`text-[10px] px-1 py-0.5 rounded border ${slotColor(a)}`}>
                      {new Date(a.start_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} {a.booked_count}/{a.max_students}
                    </div>
                  ))}
                  {d.info.availList.length > 2 && <div className="text-[10px] text-slate-500">+{d.info.availList.length - 2}</div>}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
      {selectedDay && (
        <div className="card-soft p-5 mt-4" data-testid="cal-day-detail">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h4 className="font-semibold capitalize" style={{ fontFamily: "Outfit" }}>
              {new Date(selectedDay).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </h4>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" className="rounded-full bg-sky-700 hover:bg-sky-600 text-white" onClick={() => onAddSlotForDay(selectedDay)} data-testid="add-slot-from-day-btn">
                <Plus className="w-3 h-3 mr-1" />Ajouter un créneau
              </Button>
              {selectedAvails.size > 0 && (
                <Button size="sm" className="rounded-full bg-red-600 hover:bg-red-500 text-white" onClick={openCancelDialog} data-testid="cancel-selection-btn">
                  <X className="w-3 h-3 mr-1" />Annuler la sélection ({selectedAvails.size})
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setSelectedDay(null)}><X className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="space-y-2">
            {(dayMap[selectedDay]?.availList || []).length === 0 && (
              <div className="text-center py-6 text-sm text-slate-500" data-testid="cal-day-empty">
                Aucun créneau ce jour-là. Cliquez « <strong>Ajouter un créneau</strong> » ci-dessus pour en créer un.
              </div>
            )}
            {(dayMap[selectedDay]?.availList || []).map((a) => {
              const bks = slotBookings(a.avail_id);
              const selected = selectedAvails.has(a.avail_id);
              return (
                <div key={a.avail_id} className={`border rounded-xl p-3 ${slotColor(a)} ${selected ? "ring-2 ring-red-400" : ""}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={selected} onChange={() => toggleSelect(a.avail_id)}
                        className="w-4 h-4 accent-red-600" data-testid={`cal-select-${a.avail_id}`} />
                      <div className="font-semibold">
                        {new Date(a.start_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} → {new Date(a.end_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {a.hidden_by_mns && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 font-medium" data-testid={`hidden-badge-${a.avail_id}`} title="Ce créneau est caché aux clients jusqu'à ce que les précédents soient réservés">
                          👁‍🗨 Caché
                        </span>
                      )}
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => toggleHidden(a.avail_id, !!a.hidden_by_mns)}
                        className="text-xs px-2 py-1 rounded-full border border-slate-300 hover:bg-slate-100 transition"
                        data-testid={`toggle-hidden-${a.avail_id}`}
                        title={a.hidden_by_mns
                          ? "Rendre visible aux clients dès maintenant"
                          : "Cacher ce créneau jusqu'à ce que les précédents soient réservés"}
                      >
                        {a.hidden_by_mns ? "👁 Rendre visible" : "👁‍🗨 Cacher"}
                      </button>
                      <div className="text-xs">{a.booked_count}/{a.max_students} réservé(s)</div>
                    </div>
                  </div>
                  {bks.length > 0 && (
                    <div className="mt-2 space-y-1 text-xs bg-white/60 rounded p-2">
                      {bks.map((b) => (
                        <div key={b.booking_id} className="flex items-center justify-between">
                          <span>👤 {b.client?.name} • {b.age_group === "child" ? "Enfant" : "Adulte"} • {b.level || "—"}</span>
                          <span className="text-slate-500">{b.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={(v) => { if (!canceling) setCancelOpen(v); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Annuler {confirmedToCancel.size} créneau(x)</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Vérifiez la liste ci-dessous — décochez ceux que vous voulez conserver.
            Les clients concernés seront notifiés par email + SMS + notification in-app.
            Les paiements en ligne seront <strong>remboursés automatiquement</strong> sur leur cagnotte.
          </p>
          <div className="space-y-3">
            {/* Iter43 — recap "panier" : list of slots, each with checkbox (default checked) */}
            <div className="border rounded-xl p-3 bg-slate-50 max-h-56 overflow-auto" data-testid="cancel-recap">
              <div className="text-xs font-semibold text-slate-700 mb-2">Créneaux sélectionnés ({selectedAvails.size})</div>
              {Array.from(selectedAvails).map((aid) => {
                const a = avails.find((x) => x.avail_id === aid);
                if (!a) return null;
                const checked = confirmedToCancel.has(aid);
                const bks = slotBookings(aid);
                const start = new Date(a.start_dt);
                return (
                  <label key={aid} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-sm hover:bg-white transition ${checked ? "" : "opacity-50"}`} data-testid={`cancel-row-${aid}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleConfirmCancel(aid)} className="mt-0.5 w-4 h-4 accent-red-600" data-testid={`cancel-row-cb-${aid}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">
                        {start.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })} • {start.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} → {new Date(a.end_dt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="text-xs text-slate-500">
                        {a.booked_count}/{a.max_students} réservé(s)
                        {bks.length > 0 && <> • {bks.slice(0, 2).map((b) => b.client?.name).join(", ")}{bks.length > 2 ? ` +${bks.length - 2}` : ""}</>}
                      </div>
                    </div>
                  </label>
                );
              })}
              {selectedAvails.size === 0 && (
                <div className="text-xs text-slate-400 italic">Aucun créneau sélectionné.</div>
              )}
            </div>
            <div>
              <Label>Motif <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[["technical","Problème technique"],["mns_absent","Absence du MNS"],["other","Autre"]].map(([v, l]) => (
                  <label key={v} className={`border rounded-xl p-2 text-xs text-center cursor-pointer transition ${cancelForm.reason_code === v ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 hover:border-red-300"}`}>
                    <input type="radio" className="sr-only" name="reason" checked={cancelForm.reason_code === v}
                      onChange={() => setCancelForm({ ...cancelForm, reason_code: v })}
                      data-testid={`cancel-reason-${v}`} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Message personnalisé {cancelForm.reason_code === "other" && <span className="text-red-500">*</span>}<span className="text-xs text-slate-400 ml-1">(optionnel — transmis aux clients)</span></Label>
              <Textarea rows={3} value={cancelForm.custom_text}
                onChange={(e) => setCancelForm({ ...cancelForm, custom_text: e.target.value })}
                placeholder="Ex: Piscine fermée pour maintenance, désolé."
                data-testid="cancel-custom-text" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCancelOpen(false)} disabled={canceling}>Retour</Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-500 text-white" onClick={doCancel} disabled={canceling || confirmedToCancel.size === 0} data-testid="cancel-confirm-btn">
              {canceling ? "Annulation…" : `Annuler ${confirmedToCancel.size} créneau(x)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/reviews/me").then((r) => setReviews(r.data)).catch(() => {}); }, []);
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;
  return (
    <div className="mt-6" data-testid="mns-reviews">
      <div className="card-soft p-5 mb-4">
        <h3 className="font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
          Avis reçus ({reviews.length})
          {avg && <span className="text-slate-500 text-sm font-normal ml-2">moyenne : {avg}/5</span>}
        </h3>
        <p className="text-xs text-slate-500 mt-1">Pour préserver l'anonymat, le nom du client est masqué.</p>
      </div>
      {reviews.length === 0 && <div className="card-soft p-8 text-center text-slate-500">Aucun avis pour le moment.</div>}
      <div className="grid sm:grid-cols-2 gap-3">
        {reviews.map((r) => (
          <div key={r.review_id} className="card-soft p-4">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900">{r.client_name}</div>
              <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>
            </div>
            {r.comment && <p className="text-sm text-slate-600 mt-2">{r.comment}</p>}
            <div className="text-xs text-slate-400 mt-2">{new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}


function WalletTab() {
  const [wallet, setWallet] = useState(null);
  const [boost, setBoost] = useState(null);
  const [referralCredits, setReferralCredits] = useState(0);
  const confirm = useConfirm();
  const now = new Date();
  const [stmtYear, setStmtYear] = useState(now.getFullYear());
  const [stmtMonth, setStmtMonth] = useState(now.getMonth() + 1);
  const [stmtIncome, setStmtIncome] = useState("all");
  const [stmtPreview, setStmtPreview] = useState(null);
  const [stmtDenied, setStmtDenied] = useState(false);
  const [txPage, setTxPage] = useState(0);
  const [txSize, setTxSize] = useState(20);
  const reload = async () => {
    try {
      setWallet((await api.get("/wallet")).data);
    } catch (e) {
      // Iter51: cagnotte est désormais gratuite ; en cas d'erreur, on reste sur un état vide.
      setWallet({ balance: 0, transactions: [] });
    }
    setBoost((await api.get("/boost/status")).data);
    try {
      const { data } = await api.get("/referral/me");
      setReferralCredits(Number(data?.referral_credits || 0));
    } catch { /* client-only endpoint on MNS returns 200 too */ }
  };
  useEffect(() => { reload(); }, []);

  const fetchStmt = async () => {
    try {
      setStmtDenied(false);
      const { data } = await api.get("/statements/monthly", {
        params: { year: stmtYear, month: stmtMonth, format: "json", income_type: stmtIncome },
      });
      setStmtPreview(data);
    } catch (e) {
      if (e.response?.status === 403) {
        setStmtDenied(true);
        setStmtPreview(null);
      } else {
        toast.error("Erreur");
      }
    }
  };
  useEffect(() => { fetchStmt(); /* eslint-disable-next-line */ }, [stmtYear, stmtMonth, stmtIncome]);

  const doDownloadStatement = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/statements/monthly?year=${stmtYear}&month=${stmtMonth}&format=csv&income_type=${stmtIncome}`;
    window.open(url, "_blank");
  };

  const redeemBoostWithCredits = async () => {
    const ok = await confirm({
      title: "Utiliser mes crédits parrainage pour le boost ?",
      body: `${boost?.credits_cost || 100} crédits seront débités de votre solde. Le boost de 7 jours sera activé immédiatement.`,
      confirmLabel: "Activer le boost",
    });
    if (!ok) return;
    try {
      await api.post("/boost/redeem-credits");
      toast.success("Boost activé avec vos crédits parrainage !");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const downloadCSV = () => doDownloadStatement();

  const downloadPDF = async () => {
    if (!stmtPreview) return;
    const d = stmtPreview;
    const doc = new jsPDF();
    const last = new Date(d.period.end); last.setDate(last.getDate() - 1);
    doc.setFillColor(3, 105, 161); doc.rect(0, 0, 210, 24, "F");
    doc.setTextColor(255); doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text("RELEVÉ MENSUEL — MySwimmingLessons", 14, 15);
    doc.setTextColor(0); doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.text(`MNS : ${d.mns.name} (${d.mns.email})`, 14, 32);
    doc.text(`Période : du ${new Date(d.period.start).toLocaleDateString("fr-FR")} au ${last.toLocaleDateString("fr-FR")}`, 14, 38);
    let y = 50;
    if (!d.rows || d.rows.length === 0) {
      doc.setFontSize(12); doc.text("Aucune donnée disponible.", 14, y);
    } else {
      doc.setFontSize(9); doc.setFont("helvetica", "bold");
      doc.setFillColor(240, 249, 255); doc.rect(14, y - 5, 182, 7, "F");
      doc.text("Date", 16, y);
      doc.text("Client", 55, y);
      doc.text("Heures", 105, y);
      doc.text("Tarif HT", 130, y);
      doc.text("Mode", 155, y);
      doc.text("Statut", 175, y);
      doc.setFont("helvetica", "normal");
      y += 7;
      d.rows.forEach((r) => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(String(r.date), 16, y);
        doc.text(String(r.client).slice(0, 22), 55, y);
        doc.text(String(r.hours), 105, y);
        doc.text(`${r.base_amount} €`, 130, y);
        doc.text(String(r.payment_method), 155, y);
        doc.text(String(r.status), 175, y);
        y += 5;
      });
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.text("TOTAUX", 14, y); y += 6;
      doc.setFont("helvetica", "normal");
      doc.text(`CA cours validés : ${d.totals.total_base} €`, 14, y); y += 5;
      doc.text(`  dont en ligne : ${d.totals.total_online} €`, 14, y); y += 5;
      doc.text(`  dont espèces : ${d.totals.total_cash} €`, 14, y); y += 5;
      doc.text(`Pénalités no-show (part MNS) : ${d.totals.no_show_share} €`, 14, y); y += 7;
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text(`TOTAL période : ${d.totals.grand_total} €`, 14, y);
    }
    doc.save(`releve_${d.period.year}_${String(d.period.month).padStart(2, "0")}.pdf`);
  };

  const buyBoost = async (method) => {
    const ok = await confirm({
      title: method === "stripe" ? "Acheter le boost par carte ?" : "Utiliser ma cagnotte ?",
      body: method === "stripe"
        ? `Vous serez redirigé vers Stripe pour payer ${boost?.price?.toFixed(2)}€. Le boost de 7 jours sera activé après paiement.`
        : `${boost?.price?.toFixed(2)}€ seront déduits de votre cagnotte (solde actuel : ${wallet.balance.toFixed(2)}€). Le boost sera activé immédiatement.`,
      confirmLabel: "Activer le boost",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/boost/activate", { payment_method: method, origin_url: window.location.origin });
      if (method === "stripe" && data.url) { redirectToCheckout(data.url); return; }
      toast.success("Boost activé !"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!wallet) return null;
  const creditsBalance = Number(referralCredits || 0);
  const boostCreditsCost = Number(boost?.credits_cost || 100);
  const canRedeemBoost = !boost?.active && creditsBalance >= boostCreditsCost;
  return (
    <div className="mt-6 space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card-soft p-5">
          <div className="flex items-center justify-between"><Wallet className="w-5 h-5 text-sky-700" /><span className="text-xs uppercase tracking-widest text-slate-500">Cagnotte</span></div>
          <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{wallet.balance.toFixed(2)}€</div>
          <div className="text-xs text-slate-500 mt-1">Utilisable pour le boost ou l'abonnement Premium — non convertible en argent.</div>
        </div>
        <div className="card-soft p-5">
          <div className="flex items-center justify-between"><Star className="w-5 h-5 text-amber-500" /><span className="text-xs uppercase tracking-widest text-slate-500">Boost visibilité (7 jours)</span></div>
          {boost?.active ? (
            <>
              <div className="text-base font-semibold text-emerald-700 mt-2">Actif</div>
              <div className="text-xs text-slate-500">Jusqu'au {new Date(boost.boost.expires_at).toLocaleDateString("fr-FR")}</div>
            </>
          ) : (
            <>
              <div className="text-base font-semibold text-slate-700 mt-2">Inactif</div>
              <div className="text-xs text-slate-500">{boost?.price?.toFixed(2)}€ / {boost?.days} jours</div>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Button size="sm" className="rounded-full bg-sky-700 hover:bg-sky-600" onClick={() => buyBoost("stripe")} data-testid="boost-stripe-dash">Carte</Button>
                <Button size="sm" variant="outline" className="rounded-full" disabled={(wallet.balance || 0) < (boost?.price || 9.99)} onClick={() => buyBoost("wallet")} data-testid="boost-wallet-dash">Cagnotte</Button>
                <Button
                  size="sm" variant="outline"
                  className={`rounded-full ${canRedeemBoost ? "border-emerald-500 text-emerald-700 hover:bg-emerald-50" : "opacity-50 cursor-not-allowed"}`}
                  disabled={!canRedeemBoost}
                  onClick={redeemBoostWithCredits}
                  data-testid="boost-credits-dash"
                  title={canRedeemBoost ? "" : `Requiert ${boostCreditsCost} crédits (solde : ${creditsBalance})`}
                >
                  {boostCreditsCost} crédits ({creditsBalance}/{boostCreditsCost})
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <StripeConnectCard />

      {!stmtDenied && (
      <div className="card-soft p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Download className="w-5 h-5" />Relevé mensuel pour comptable</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={String(stmtMonth)} onValueChange={(v) => setStmtMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px] rounded-full" data-testid="stmt-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"].map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(stmtYear)} onValueChange={(v) => setStmtYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] rounded-full" data-testid="stmt-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[now.getFullYear() - 1, now.getFullYear()].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={stmtIncome} onValueChange={setStmtIncome}>
              <SelectTrigger className="w-[160px] rounded-full" data-testid="stmt-income-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous revenus</SelectItem>
                <SelectItem value="online">En ligne (Stripe)</SelectItem>
                <SelectItem value="cash">Espèces</SelectItem>
                <SelectItem value="no_show">Pénalités no-show</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={downloadPDF} disabled={!stmtPreview} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="download-pdf"><Download className="w-4 h-4 mr-1" />PDF</Button>
            <Button onClick={downloadCSV} variant="outline" className="rounded-full" data-testid="download-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
          </div>
        </div>
        {stmtPreview && (
          stmtPreview.rows.length === 0 ? (
            <div className="text-center text-slate-500 py-8" data-testid="stmt-empty">Aucune donnée disponible.</div>
          ) : (
            <div className="text-sm" data-testid="stmt-preview">
              <div className="text-xs text-slate-500 mb-2">Période du {new Date(stmtPreview.period.start).toLocaleDateString("fr-FR")} au {(() => { const d = new Date(stmtPreview.period.end); d.setDate(d.getDate() - 1); return d.toLocaleDateString("fr-FR"); })()}</div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-slate-50 p-3 rounded-lg"><div className="text-xs text-slate-500">CA validé</div><div className="font-bold">{stmtPreview.totals.total_base}€</div></div>
                <div className="bg-slate-50 p-3 rounded-lg"><div className="text-xs text-slate-500">Pénalités MNS</div><div className="font-bold">{stmtPreview.totals.no_show_share}€</div></div>
                <div className="bg-sky-50 p-3 rounded-lg col-span-2"><div className="text-xs text-sky-700">TOTAL</div><div className="font-bold text-lg text-sky-900">{stmtPreview.totals.grand_total}€</div></div>
              </div>
            </div>
          )
        )}
        <p className="text-xs text-slate-500 mt-3">Une notification email vous sera envoyée le 2 du mois suivant lorsqu'un nouveau relevé sera disponible (à activer dans votre profil).</p>
      </div>
      )}

      <div className="card-soft p-5">
        <h3 className="font-semibold mb-3" style={{ fontFamily: "Outfit" }}>Historique des transactions</h3>
        <div className="space-y-1">
          {wallet.transactions.length === 0 && <div className="text-slate-500 text-sm">Aucune transaction.</div>}
          {wallet.transactions.slice(txPage * txSize, (txPage + 1) * txSize).map((t) => (
            <div key={t.tx_id} className="flex items-center justify-between border-b py-2 text-sm">
              <div>
                <div className="font-medium text-slate-900">{t.description}</div>
                <div className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString("fr-FR")} • {t.kind}</div>
              </div>
              <div className={`font-semibold ${t.amount >= 0 ? "text-emerald-700" : "text-red-700"}`}>{t.amount >= 0 ? "+" : ""}{t.amount.toFixed(2)}€</div>
            </div>
          ))}
        </div>
        <PaginationBar
          page={txPage} setPage={setTxPage}
          totalPages={Math.max(1, Math.ceil(wallet.transactions.length / txSize))}
          pageSize={txSize} setPageSize={(v) => { setTxSize(v); setTxPage(0); }}
          total={wallet.transactions.length} testId="wallet-tx-pager"
        />
      </div>
    </div>
  );
}


function StripeConnectCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [pendingPlatform, setPendingPlatform] = useState(null); // { message, admin_url }
  const confirm = useConfirm();

  const reload = async () => {
    try {
      const { data } = await api.get("/mns/connect/status");
      setSt(data);
    } catch { setSt({ has_account: false }); }
  };
  useEffect(() => { reload(); }, []);

  const startOnboarding = async () => {
    setBusy(true);
    setPendingPlatform(null);
    try {
      const { data } = await api.post("/mns/connect/account-link");
      window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured error from backend → render inline help instead of a toast
      if (detail && typeof detail === "object" && detail.code === "platform_profile_pending") {
        setPendingPlatform({
          message: detail.message,
          admin_url: detail.admin_url,
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur Stripe");
      }
    } finally { setBusy(false); }
  };

  const openDashboard = async () => {
    try {
      const { data } = await api.post("/mns/connect/dashboard-link");
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const doWithdraw = async () => {
    const amt = parseFloat(withdrawAmt);
    if (!(amt > 0)) return toast.error("Montant invalide");
    if (amt > st.balance_available) return toast.error("Montant supérieur au solde disponible");
    const ok = await confirm({
      title: "Confirmer le versement",
      body: `${amt.toFixed(2)}€ seront transférés vers votre compte Stripe (virement final sous 1 à 3 jours ouvrés).`,
      confirmLabel: "Confirmer",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/mns/connect/withdraw", { amount: amt });
      toast.success("Versement lancé");
      setWithdrawAmt("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  if (!st) return <div className="card-soft p-5 text-sm text-slate-500">Chargement…</div>;

  // Not yet onboarded
  if (!st.has_account) {
    return (
      <div className="card-soft p-5 border-l-4 border-l-sky-600" data-testid="connect-card-not-onboarded">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              Versements automatisés Stripe
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Non configuré</span>
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Activez les versements automatisés : vos paiements clients seront directement envoyés sur votre compte bancaire,
              après retenue des 10&nbsp;% de commission MySwimmingLessons.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Onboarding sécurisé Stripe (5 min) : vérification d'identité + IBAN + n° SIRET.
            </p>
            {pendingPlatform && (
              <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm" data-testid="connect-platform-pending-help">
                <div className="font-semibold text-amber-900 mb-1">⚠️ Activation en attente côté plateforme</div>
                <div className="text-amber-800">{pendingPlatform.message}</div>
                <div className="mt-2 text-xs text-amber-700">
                  Administrateur MySwimmingLessons → complétez le questionnaire Connect sur{" "}
                  <a href={pendingPlatform.admin_url} target="_blank" rel="noreferrer" className="underline font-medium">
                    dashboard.stripe.com
                  </a>. L'activation est généralement instantanée après validation.
                </div>
              </div>
            )}
          </div>
          <Button onClick={startOnboarding} disabled={busy} className="rounded-full bg-sky-700 hover:bg-sky-600 self-center" data-testid="connect-onboard-btn">
            {busy ? "Redirection…" : "Configurer mes versements"}
          </Button>
        </div>
      </div>
    );
  }

  // Onboarded but incomplete (missing requirements)
  if (!st.charges_enabled || !st.payouts_enabled) {
    return (
      <div className="card-soft p-5 border-l-4 border-l-amber-500" data-testid="connect-card-incomplete">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              Versements Stripe
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Onboarding incomplet</span>
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Stripe a besoin d'informations supplémentaires pour activer vos versements automatisés.
            </p>
            {(st.requirements || []).length > 0 && (
              <div className="mt-2 text-xs text-slate-500">
                À compléter : {(st.requirements || []).join(", ")}
              </div>
            )}
          </div>
          <Button onClick={startOnboarding} disabled={busy} className="rounded-full bg-amber-600 hover:bg-amber-700 self-center" data-testid="connect-complete-btn">
            {busy ? "Redirection…" : "Compléter l'onboarding"}
          </Button>
        </div>
      </div>
    );
  }

  // Fully active
  return (
    <div className="card-soft p-5 border-l-4 border-l-emerald-500" data-testid="connect-card-active">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            Versements Stripe
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Actif</span>
          </h3>
          <div className="text-sm text-slate-600 mt-1">
            Vos paiements clients sont automatiquement crédités sur votre solde Stripe.
          </div>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={openDashboard} data-testid="connect-dashboard-btn">
          Dashboard Stripe ↗
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div className="bg-emerald-50 rounded-xl p-4">
          <div className="text-xs text-emerald-700 uppercase tracking-widest">Solde disponible</div>
          <div className="text-3xl font-bold text-emerald-900 mt-1" data-testid="connect-balance-available">{(st.balance_available || 0).toFixed(2)}€</div>
        </div>
        <div className="bg-sky-50 rounded-xl p-4">
          <div className="text-xs text-sky-700 uppercase tracking-widest">En attente</div>
          <div className="text-3xl font-bold text-sky-900 mt-1" data-testid="connect-balance-pending">{(st.balance_pending || 0).toFixed(2)}€</div>
        </div>
      </div>
      <div className="mt-4">
        <Label className="text-sm font-semibold">Demander un versement</Label>
        <div className="flex gap-2 mt-1">
          <Input
            type="number" step="0.01" placeholder="Montant"
            value={withdrawAmt}
            onChange={(e) => setWithdrawAmt(e.target.value)}
            data-testid="connect-withdraw-amount"
          />
          <Button onClick={doWithdraw} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="connect-withdraw-btn">
            Virer sur mon compte
          </Button>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Disponible en 1 à 3 jours ouvrés sur votre IBAN (géré par Stripe).
        </div>
      </div>
    </div>
  );
}


// ---------- Espace Comptable ----------
function AccountingTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error403, setError403] = useState(false);

  useEffect(() => {
    setLoading(true); setError403(false);
    api.get(`/mns/accounting/summary?year=${year}&month=${month}`)
      .then((r) => setSummary(r.data))
      .catch((e) => {
        if (e.response?.status === 403) { setError403(true); return; }
        console.warn("accounting summary failed", e);
        setSummary(null);
      })
      .finally(() => setLoading(false));
  }, [year, month]);

  if (error403) return <PremiumPaywall feature="L'Espace Comptabilité" />;

  const downloadPack = async () => {
    try {
      setDownloading(true);
      const { data, headers } = await api.get(
        `/mns/accounting/monthly-pack?year=${year}&month=${month}`,
        { responseType: "blob" }
      );
      const blob = new Blob([data], { type: "application/zip" });
      const dispo = headers["content-disposition"] || "";
      const m = dispo.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `MSL_Comptabilite_${year}${String(month).padStart(2, "0")}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("Pack comptable téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Téléchargement indisponible");
    } finally {
      setDownloading(false);
    }
  };

  const months = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="space-y-4" data-testid="accounting-tab">
      <div className="card-soft p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Espace Comptable</h2>
            <p className="text-sm text-slate-600 max-w-xl mt-1">
              Téléchargez votre pack mensuel conforme : factures <strong>Factur-X</strong> (PDF/A-3 + XML EN 16931),
              <strong> FEC simplifié</strong> (import Sage / Pennylane / Indy) et <strong>rapport TVA</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="rounded-xl border px-3 py-2 text-sm" data-testid="accounting-month">
              {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="rounded-xl border px-3 py-2 text-sm" data-testid="accounting-year">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-400">Calcul en cours…</div>
        ) : summary ? (
          <div className="grid sm:grid-cols-4 gap-3">
            <Kpi label="Factures du mois" value={summary.completed_count} testid="acc-kpi-count" />
            <Kpi label="Total HT" value={`${summary.total_ht.toFixed(2)} €`} testid="acc-kpi-ht" />
            <Kpi label="Total TVA" value={`${summary.total_tva.toFixed(2)} €`} testid="acc-kpi-tva" />
            <Kpi label="Total TTC" value={`${summary.total_ttc.toFixed(2)} €`} testid="acc-kpi-ttc" />
          </div>
        ) : null}

        {summary && !summary.vat_subject && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            Vous êtes en <strong>franchise en base de TVA</strong> (art. 293 B CGI) — aucune TVA à déclarer.
            Vos factures portent la mention obligatoire.
          </div>
        )}
        {summary && summary.vat_subject && (
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-900">
            <strong>Option TVA</strong> : {summary.vat_option_debits ? "sur les débits (émission)" : "sur les encaissements (par défaut)"}.
            Taux : {summary.vat_rate}%.
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={downloadPack}
            disabled={downloading || !summary || summary.completed_count === 0}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="download-accounting-pack"
          >
            <FileText className="w-4 h-4 mr-1" />
            {downloading ? "Génération du pack…" : `Télécharger le pack ${months[month - 1]} ${year}`}
          </Button>
        </div>

        <div className="text-[11px] text-slate-500 leading-relaxed border-t pt-3">
          <strong>Contenu du ZIP :</strong><br />
          • <code>factures/</code> — toutes vos factures Factur-X du mois (PDF/A-3 + XML CII EN 16931)<br />
          • <code>FEC_{year}{String(month).padStart(2, "0")}.csv</code> — journal des ventes (format FEC-light, séparateur |)<br />
          • <code>Rapport_TVA_{year}{String(month).padStart(2, "0")}.txt</code> — synthèse TVA pour votre déclaration<br />
          <em>Rapport fourni à titre indicatif. Consultez votre expert-comptable avant toute déclaration fiscale.</em>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3" data-testid={testid}>
      <div className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{value}</div>
    </div>
  );
}
